import React, { useState, useEffect } from "react";
import QurealSection from "../common/QurealSection";
import SectionHeader from "../components/commons/SectionHeader";
import { FileImageOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Input } from "antd";
import CreationsPicker from "../my_collections/CreationsPicker";
import RelationFieldStandard from "../qforms/standard_elements/RelationFieldStandard";
import Lottie from "react-lottie-player";
import loadingAnimation from "../trends/loader.json";
import { useBotEndpoint, usemutationBot } from "../_actions/bot";
import PreviewTemplate from "../qmade/creator/PreviewTemplate";
import { getImageURL, getPublicImageURL } from "../_helpers/image";
import { useSingleItem } from "../_actions/app/items";
import MyCreations from "../creations/MyCreations";
import { getHeroIcon } from "../_helpers/heroicons";

const FESTIVAL_FIELD = {
    foreign_key_table: "qdb_event",
    foreign_key_column: "id",
    type: "relationM2O",
    template: "{{name}}",
};

function FactoryHome({ me }) {
    const [form] = Form.useForm();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);
    const [creationId, setCreationId] = useState(null);
    const [selectedFestival, setSelectedFestival] = useState(null);

    const { data: creationData } = useSingleItem(
        "qp_creation",
        `/${creationId}?fields=*,used_schemas.qdb_creation_schema_id.*`,
        !!creationId,
    );

    useEffect(() => {
        if (creationData?.data) {
            setPreviewItem(creationData);
            setPreviewOpen(true);
        }
    }, [creationData]);

    const mutationBot = useBotEndpoint(
        "/q0/generate_festival_design",
        (result) => {
            console.log("Generate result", result);
            if (result?.success && result?.data?.id) {
                setCreationId(result.data.id);
            }
        },
    );

    // const genQ0P1 = usemutationBot((result) => {
    //     console.log("Generate result", result);
    //     if (result?.success && result?.data?.id) {
    //         setCreationId(result.data.id);
    //     }
    // });

    const creations = Form.useWatch("creations", form);
    const festival = Form.useWatch("festival", form);
    const canGenerate = !!creations?.[0]?.id && !!festival;

    const handleGenerate = () => {
        const values = form.getFieldsValue();
        mutationBot.mutate({
            base_creation_id: values.creations?.[0]?.id,
            prompt: values.prompt,
            // publish: true,
            skip_image_gen: false,
            enable_color_change: values.enable_color_change ?? false,
            enable_font_change: values.enable_font_change ?? false,
        });
    };

    return (
        <>
            <QurealSection
                header={
                    <SectionHeader
                        heading="Festival Post Generator"
                        icon={<FileImageOutlined />}
                    />
                }
                footer={false}
            >
                <div
                    style={{
                        display: "flex",
                        gap: "1.5rem",
                        padding: "1rem",
                        position: "relative",
                    }}
                >
                    {mutationBot.isLoading && (
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(255,255,255,0.8)",
                                zIndex: 10,
                            }}
                        >
                            <Lottie
                                animationData={loadingAnimation}
                                loop
                                play
                                style={{ width: 100, height: 100 }}
                            />
                            <p style={{ marginTop: 8, color: "#555" }}>
                                Generating...
                            </p>
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={{
                                enable_color_change: true,
                                enable_font_change: true,
                            }}
                        >
                            <Form.Item
                                label="Select Base Creation"
                                name="creations"
                            >
                                <CreationsPicker
                                    allow_template={false}
                                    allow_multiple={false}
                                    creations_filter_query="filter[is_base][_eq]=true"
                                />
                            </Form.Item>
                            <Form.Item label="Festival" name="festival">
                                <RelationFieldStandard
                                    field={FESTIVAL_FIELD}
                                    fieldString="filter[event_type][_eq]=festival&limit=-1"
                                    value={undefined}
                                    onChange={(val) =>
                                        form.setFieldValue("festival", val)
                                    }
                                    onChangeFull={(item) => {
                                        setSelectedFestival(item ?? null);
                                        if (item?.name) {
                                            form.setFieldValue(
                                                "prompt",
                                                `Make post for ${item.name}`,
                                            );
                                        }
                                    }}
                                    placeholder="Select a festival"
                                />
                            </Form.Item>
                            <Form.Item label="Prompt" name="prompt">
                                <Input.TextArea
                                    rows={4}
                                    placeholder="Enter prompt..."
                                />
                            </Form.Item>

                            <Form.Item
                                name="enable_color_change"
                                valuePropName="checked"
                            >
                                <Checkbox>Enable Color Change</Checkbox>
                            </Form.Item>
                            <Form.Item
                                name="enable_font_change"
                                valuePropName="checked"
                            >
                                <Checkbox>Enable Font Change</Checkbox>
                            </Form.Item>
                            <Form.Item>
                                <Button
                                    type="primary"
                                    onClick={handleGenerate}
                                    loading={mutationBot.isLoading}
                                    disabled={!canGenerate}
                                    icon={getHeroIcon("sparkles", {
                                        width: "18px",
                                    })}
                                >
                                    Generate
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                    {previewItem?.preview && (
                        <div
                            style={{
                                flex: 1,
                                minWidth: 0,
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "center",
                            }}
                        >
                            <img
                                src={getPublicImageURL(previewItem.preview)}
                                alt="Generated design preview"
                                onClick={() => setPreviewOpen(true)}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: 400,
                                    borderRadius: 8,
                                    boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                                    cursor: "pointer",
                                    objectFit: "contain",
                                }}
                            />
                        </div>
                    )}
                </div>
            </QurealSection>
            {previewItem && (
                <PreviewTemplate
                    item={previewItem}
                    open={previewOpen}
                    setOpen={setPreviewOpen}
                    is_template={false}
                    slug_field="slug"
                    enable_img_preview={false}
                    preset={
                        previewItem?.used_schemas?.[0]?.qdb_creation_schema_id
                    }
                    me={me}
                    default_values_for_publish={{
                        title: selectedFestival?.name
                            ? `${selectedFestival.name} post`
                            : undefined,
                        primary_tag: selectedFestival?.primary_tag,
                    }}
                />
            )}

            <QurealSection
                header={
                    <SectionHeader
                        heading="Base Designs"
                        icon={<FileImageOutlined />}
                    />
                }
                footer={false}
            >
                <div style={{ padding: "15px" }}>
                    <MyCreations
                        type="all_creation"
                        me={me}
                        user="allall"
                        // initial_preset={preset}
                        show_folder_bar={false}
                        show_preset_filter={false}
                        show_create_option={false}
                        show_filters={false}
                        applied_filters={[{ is_base: { _eq: true } }]}
                    />
                </div>
            </QurealSection>

            <QurealSection
                header={
                    <SectionHeader
                        heading="AI Generated Designs"
                        icon={<FileImageOutlined />}
                    />
                }
            >
                <div style={{ padding: "15px" }}>
                    <MyCreations
                        type="all_creation"
                        me={me}
                        user="allall"
                        // initial_preset={preset}
                        show_folder_bar={false}
                        show_preset_filter={false}
                        show_create_option={false}
                        show_filters={false}
                        applied_filters={[{ is_ai_generated: { _eq: true } }]}
                    />
                </div>
            </QurealSection>
        </>
    );
}

export default FactoryHome;
